import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { finalize } from 'rxjs'

import {
  KPI_ASSESSMENT_ALREADY_USED_MESSAGE,
  KPI_SHEET_NOT_FOUND_MESSAGE,
  KPI_DASHBOARD_NOT_FOUND_MESSAGE,
  KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE,
  KpiDashboardService,
  KpiFeedbackSheetCandidateView,
  KpiFeedbackSheetCandidates,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { captureException } from '../../../util/captureException'
import {
  isKpiCustomError,
  CustomError,
  CustomErrorCode,
} from '../../../util/throwIfCustomError'
import {
  automaticCandidateIds,
  CandidateSort,
  CandidateSortField,
  DEFAULT_CANDIDATE_SORT,
  sortCandidateRows,
  MAX_FEEDBACK_TARGET_COUNT,
  toggleCandidateSelection,
  orderSelectedCandidateIds,
  buildAssessmentLinkLabel,
  describeCandidateSelectionReason,
  formatFeedbackDateTime,
  formatFeedbackMeetingTitle,
  formatFeedbackOverallScore,
} from './feedback-sheet.logic'

export type NewFeedbackSheetDialogData = {
  dashboardId: string
  userId: string
} & (
  | {
      sheetId?: never
      assessmentIds?: never
      initialAssessmentIds?: never
      isAiDraftFailed?: never
      isAiDraftStale?: never
    }
  | {
      sheetId: string
      assessmentIds: string[]
      initialAssessmentIds?: string[]
      isAiDraftFailed?: boolean
      isAiDraftStale?: boolean
    }
)

export type NewFeedbackSheetDialogResult =
  | { mode: 'create'; sheetId: string }
  | { mode: 'change'; sheetId: string; assessmentIds: string[] }
  | { mode: 'missing'; error: CustomError }

type AssessmentLinkParams = { 'user-id': string; 'assessment-id': string }

interface CandidateRow {
  rank: number
  candidate: KpiFeedbackSheetCandidateView
  meetingTitleText: string
  assessDateText: string
  checkboxLabel: string
  linkLabel: string
  linkParams: AssessmentLinkParams
  overallScoreText: string
  focusScoreText: string | null
  isAutomatic: boolean
  targetNumber: number
  selectionReason: string
}

@UntilDestroy()
@Component({
  selector: 'app-new-feedback-sheet-dialog',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterLink,
  ],
  templateUrl: './new-feedback-sheet-dialog.component.html',
  styleUrl: './new-feedback-sheet-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewFeedbackSheetDialogComponent implements OnInit, OnDestroy {
  readonly maxTargetCount = MAX_FEEDBACK_TARGET_COUNT
  candidates: KpiFeedbackSheetCandidates = []
  automaticIds: string[] = []
  assessmentIds: string[] = []
  isLoading = true
  isCreating = false
  errorMessage = ''
  hasUnavailableCurrentTargets = false
  candidateRows: CandidateRow[] = []
  displayedCandidateRows: CandidateRow[] = []
  sort: CandidateSort = { ...DEFAULT_CANDIDATE_SORT }
  hasAutomaticCandidates = false
  isSelectionLimitReached = false
  showSelectionLimit = false
  selectionStatus = ''
  readonly selectionLimitMessage = `対象は最大 ${this.maxTargetCount} 件です。差し替えるときは、選択中のチェックを外してから選び直してください。`
  private readonly stickyElements = new Map<string, HTMLElement>()
  // zone.js のパッチ対象外で Angular の外で実行される。DOM の CSS 変数だけを更新し、コンポーネントの状態には触れないこと。
  private readonly stickyHeightObserver = new ResizeObserver(() => {
    for (const [cssVariable, element] of this.stickyElements) {
      this.modalBody.nativeElement.style.setProperty(
        cssVariable,
        `${element.offsetHeight}px`,
      )
    }
  })

  @ViewChild('modalBody', { static: true })
  private modalBody!: ElementRef<HTMLElement>

  /** 折り返し・通知で変わる固定領域の高さをフォーカス時のスクロール余白に反映する。 */
  @ViewChild('selectionSummary')
  private set selectionSummary(summary: ElementRef<HTMLElement> | undefined) {
    this.observeStickyHeight('--selection-summary-height', summary)
  }

  /** 折り返しで変わる列見出しの高さもフォーカス時のスクロール余白に反映する。 */
  @ViewChild('candidateHead')
  private set candidateHead(head: ElementRef<HTMLElement> | undefined) {
    this.observeStickyHeight('--candidate-head-height', head)
  }

  /** 固定要素の生成・破棄に合わせて高さの監視を入れ替える。 */
  private observeStickyHeight(
    cssVariable: '--selection-summary-height' | '--candidate-head-height',
    element: ElementRef<HTMLElement> | undefined,
  ): void {
    const previous = this.stickyElements.get(cssVariable)
    if (previous) {
      this.stickyHeightObserver.unobserve(previous)
      this.stickyElements.delete(cssVariable)
    }
    this.modalBody.nativeElement.style.removeProperty(cssVariable)
    if (element) {
      this.stickyElements.set(cssVariable, element.nativeElement)
      this.stickyHeightObserver.observe(element.nativeElement, {
        box: 'border-box',
      })
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: NewFeedbackSheetDialogData,
    private readonly dialogRef: MatDialogRef<
      NewFeedbackSheetDialogComponent,
      NewFeedbackSheetDialogResult
    >,
    private readonly kpiDashboardService: KpiDashboardService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  /** 作成対象にできるアセスメント候補を読み込む。 */
  ngOnInit(): void {
    this.loadCandidates()
  }

  /** 候補を再取得し、競合後も有効な選択を保持する。 */
  private loadCandidates(afterConflict = false): void {
    const previouslySelectedIds = [...this.assessmentIds]
    this.isLoading = true
    this.kpiDashboardService
      .feedbackSheetCandidates(
        this.data.dashboardId,
        this.data.userId,
        this.data.sheetId,
      )
      .pipe(
        finalize(() => {
          this.isLoading = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (candidates) => {
          this.candidates = candidates
          this.automaticIds =
            this.data.sheetId == null ? automaticCandidateIds(candidates) : []
          this.hasUnavailableCurrentTargets =
            this.data.assessmentIds != null &&
            this.data.assessmentIds.some(
              (id) =>
                !candidates.some(({ assessmentId }) => assessmentId === id),
            )
          this.hasAutomaticCandidates = this.automaticIds.length > 0
          this.candidateRows = candidates.map((candidate, index) => {
            const meetingTitleText = formatFeedbackMeetingTitle(
              candidate.meetingTitle,
            )
            const assessDateText = formatFeedbackDateTime(candidate.assessDate)
            return {
              rank: index + 1,
              candidate,
              meetingTitleText,
              assessDateText,
              checkboxLabel: `${meetingTitleText}（${assessDateText}）を対象にする`,
              linkLabel: buildAssessmentLinkLabel(meetingTitleText),
              linkParams: {
                'user-id': this.data.userId,
                'assessment-id': candidate.assessmentId,
              },
              overallScoreText: formatFeedbackOverallScore(
                candidate.overallScore,
              ),
              focusScoreText:
                candidate.focusKeyActionAverageScore == null
                  ? null
                  : `${candidate.focusKeyActionAverageScore.toFixed(1)} / 5`,
              isAutomatic: this.automaticIds.includes(candidate.assessmentId),
              targetNumber: 0,
              selectionReason: '',
            }
          })
          this.displayedCandidateRows = sortCandidateRows(
            this.candidateRows,
            this.sort,
          )
          this.applySelection(
            afterConflict
              ? orderSelectedCandidateIds(
                  candidates,
                  this.assessmentIds.filter((id) =>
                    candidates.some(({ assessmentId }) => assessmentId === id),
                  ),
                )
              : this.data.sheetId == null
                ? [...this.automaticIds]
                : orderSelectedCandidateIds(
                    candidates,
                    (this.data.initialAssessmentIds == null
                      ? this.data.assessmentIds
                      : this.data.initialAssessmentIds
                    ).filter((id) =>
                      candidates.some(
                        ({ assessmentId }) => assessmentId === id,
                      ),
                    ),
                  ),
          )
          if (afterConflict) {
            const refreshedCandidates = new Set(
              candidates.map(({ assessmentId }) => assessmentId),
            )
            const removed = previouslySelectedIds.filter(
              (id) => !refreshedCandidates.has(id),
            )
            this.errorMessage =
              removed.length > 0
                ? '選択したアセスメントの一部は他のシートで使用済みのため候補から外れました。候補を更新したので選び直してください。'
                : '他のユーザーの操作により作成できませんでした。候補を更新したので、もう一度お試しください。'
          }
        },
        error: (error: unknown) => {
          if (
            isKpiCustomError(error, CustomErrorCode.NOT_FOUND, [
              KPI_SHEET_NOT_FOUND_MESSAGE,
              KPI_DASHBOARD_NOT_FOUND_MESSAGE,
              KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE,
            ])
          ) {
            this.closeMissingTarget(error)
            return
          }
          captureException(error)
          this.errorMessage =
            '対象アセスメントの取得に失敗しました。時間を置いて再度お試しください。'
        },
      })
  }

  /** 対象の消失を親画面へ返してダイアログを閉じる。 */
  private closeMissingTarget(error: CustomError): void {
    this.dialogRef.close({ mode: 'missing', error })
  }

  /** ダイアログ破棄時に固定領域のサイズ監視を解除する。 */
  ngOnDestroy(): void {
    this.stickyHeightObserver.disconnect()
    this.stickyElements.clear()
  }

  /** 失敗または停滞した草案なら同一対象でも再生成できる。 */
  get canRegenerateAiDraft(): boolean {
    return (
      this.data.isAiDraftFailed === true || this.data.isAiDraftStale === true
    )
  }

  /** 1件以上の候補が選択済みで作成処理中でなければ作成を許可する。 */
  get canCreate(): boolean {
    return (
      !this.isLoading &&
      !this.isCreating &&
      this.assessmentIds.length > 0 &&
      this.assessmentIds.every((id) =>
        this.candidates.some(({ assessmentId }) => assessmentId === id),
      ) &&
      !this.isUnchangedSelectionBlocked
    )
  }

  /** FAILED・停滞中の再生成以外は、現在と同じ対象集合での確定を禁止する。 */
  get isUnchangedSelectionBlocked(): boolean {
    return (
      this.data.sheetId != null &&
      !this.canRegenerateAiDraft &&
      this.data.assessmentIds.length === this.assessmentIds.length &&
      this.data.assessmentIds.every((id) => this.assessmentIds.includes(id))
    )
  }

  /** 候補の条件・自動提案・並び順の説明を返す。 */
  get candidateExplanation(): string {
    // 候補件数 200 は backend の kpiFeedbackSheet.logic.ts の MAX_CANDIDATE_ASSESSMENT_COUNT と揃えること
    const automaticSelection =
      this.data.sheetId != null
        ? '現在の対象アセスメントを初期選択しています。'
        : this.hasAutomaticCandidates
          ? `前回のシート作成以降のアセスメントから上位 ${this.maxTargetCount} 件を初期選択しています（AI による選出ではありません）。`
          : `前回のシート作成以降のアセスメントがあれば、上位 ${this.maxTargetCount} 件を初期選択します（AI による選出ではありません）。`
    return [
      '候補: このボードのスキルマップで完了したアセスメントのうち、他のシートで対象になっていないもの（最大 200 件）。',
      '並び順: 「実施日時」「総合」「重点KA平均」のヘッダーで並べ替えられます（初期表示は実施日時の新しい順）。選出理由の「N 位」は、重点KA平均が低い順（同点なら総合スコアが低い順、重点KAのスコアが無いものは末尾）での順位です。',
      this.data.sheetId == null
        ? `自動提案: ${automaticSelection}`
        : automaticSelection,
    ].join('\n')
  }

  /** 表示順だけを切り替え、選択と自動提案に使う backend 順は維持する。 */
  onSort(field: CandidateSortField): void {
    if (this.sort.field === field) {
      this.sort = {
        field,
        direction: this.sort.direction === 'asc' ? 'desc' : 'asc',
      }
    } else {
      // 1on1 に近い商談を探しやすいよう、実施日時へ切り替えた時は新しい順にする。
      this.sort = { field, direction: field === 'assessDate' ? 'desc' : 'asc' }
    }
    this.displayedCandidateRows = sortCandidateRows(
      this.candidateRows,
      this.sort,
    )
    this.changeDetectorRef.markForCheck()
  }

  /** ソート状態に対応する Material Icons のアイコン名を返す。 */
  sortIcon(field: CandidateSortField): string {
    if (this.sort.field !== field) return 'unfold_more'
    return this.sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'
  }

  /** 列見出しに現在のソート方向を読み上げ用属性として設定する。 */
  ariaSort(field: CandidateSortField): 'ascending' | 'descending' | 'none' {
    if (this.sort.field !== field) return 'none'
    return this.sort.direction === 'asc' ? 'ascending' : 'descending'
  }

  /** 上限時はクリックを取り消して通知し、それ以外は backend 順に選択を切り替える。 */
  toggleCandidate(assessmentId: string, event: MouseEvent): void {
    if (
      this.isSelectionLimitReached &&
      !this.assessmentIds.includes(assessmentId)
    ) {
      event.preventDefault()
      this.showSelectionLimit = true
      return
    }
    try {
      this.applySelection(
        orderSelectedCandidateIds(
          this.candidates,
          toggleCandidateSelection(this.assessmentIds, assessmentId),
        ),
      )
    } catch (error) {
      event.preventDefault()
      throw error
    }
  }

  /** 選択IDと表示をまとめて更新し、上限通知を解除する。 */
  private applySelection(ids: string[]): void {
    this.assessmentIds = ids
    this.showSelectionLimit = false
    this.updateSelectionDisplay()
  }

  /** 初期化・選択変更時だけ対象番号、選出理由、選択状況の表示を更新する。 */
  private updateSelectionDisplay(): void {
    const titles: string[] = []
    for (const row of this.candidateRows) {
      row.targetNumber =
        this.assessmentIds.indexOf(row.candidate.assessmentId) + 1
      row.selectionReason =
        this.data.sheetId != null &&
        this.data.assessmentIds.includes(row.candidate.assessmentId)
          ? '現在の対象'
          : row.targetNumber > 0
            ? describeCandidateSelectionReason(
                this.candidates,
                this.automaticIds,
                row.candidate.assessmentId,
              )
            : ''
      if (row.targetNumber > 0) {
        titles.push(`${row.meetingTitleText}（${row.assessDateText}）`)
      }
    }
    const count = this.assessmentIds.length
    this.isSelectionLimitReached = count >= this.maxTargetCount
    const countText = `選択中 ${count} / ${this.maxTargetCount} 件`
    this.selectionStatus =
      titles.length === 0 ? countText : `${countText}: ${titles.join('、')}`
  }

  /** 作成状態と選択件数から作成ボタンの文言を決める。 */
  get createButtonLabel(): string {
    if (this.data.sheetId != null) {
      return this.canRegenerateAiDraft ? 'AI 草案を作り直す' : '変更する'
    }
    if (this.isCreating) {
      return '作成中...'
    }
    if (this.candidates.length === 0) {
      return 'シートを作成'
    }
    return this.assessmentIds.length === 0
      ? '対象を選んでください'
      : `この${this.assessmentIds.length}件で作成`
  }

  /** モーダルを結果なしで閉じる。 */
  close(): void {
    if (!this.isCreating) {
      this.dialogRef.close()
    }
  }

  /** 新規作成を実行し、変更モードでは確認用の対象 ID を親画面へ返す。 */
  create(): void {
    if (!this.canCreate) {
      return
    }

    if (this.data.sheetId != null) {
      this.dialogRef.close({
        mode: 'change',
        sheetId: this.data.sheetId,
        assessmentIds: [...this.assessmentIds],
      })
      return
    }
    this.isCreating = true
    this.errorMessage = ''
    this.kpiDashboardService
      .createFeedbackSheet(
        this.data.dashboardId,
        this.data.userId,
        this.assessmentIds,
      )
      .pipe(
        finalize(() => {
          this.isCreating = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (sheetId) => this.dialogRef.close({ mode: 'create', sheetId }),
        error: (error: unknown) => {
          if (
            isKpiCustomError(error, CustomErrorCode.BAD_REQUEST, [
              KPI_ASSESSMENT_ALREADY_USED_MESSAGE,
            ])
          ) {
            this.loadCandidates(true)
            return
          }
          captureException(error)
          this.errorMessage =
            'フィードバックシートを作成できませんでした。入力内容を確認して再度お試しください。'
        },
      })
  }
}
